/**
 * ModuleDependencyEditor - Component for defining module dependencies
 *
 * Allows administrators to define which modules require other modules.
 * E.g., "Sinistres" requires "Véhicules"
 */

import React, { useState } from 'react';
import { Plus, X, ArrowRight, AlertCircle, Link2 } from 'lucide-react';
import { useAppFeatures } from '../../hooks/useAppFeatures.js';

// Default dependencies (informational)
const DEFAULT_DEPENDENCIES = {
  claims: ['vehicles'],
  insurance: ['vehicles'],
  fuel: ['vehicles', 'drivers'],
  inspections: ['vehicles'],
  maintenance: ['vehicles'],
  telematics: ['vehicles'],
  reports: ['vehicles'],
};

export function ModuleDependencyEditor({ dependencies = {}, onChange, selectedModules = [] }) {
  const { features } = useAppFeatures();

  function getModuleName(id) {
    return features.find(m => m.id === id)?.label || id;
  }
  const [addingFor, setAddingFor] = useState(null);
  const [newDependency, setNewDependency] = useState('');

  // Merge default dependencies with custom ones
  const effectiveDependencies = { ...DEFAULT_DEPENDENCIES, ...dependencies };

  const handleAddDependency = (moduleId) => {
    if (!newDependency || newDependency === moduleId) return;

    const currentDeps = dependencies[moduleId] || DEFAULT_DEPENDENCIES[moduleId] || [];
    if (currentDeps.includes(newDependency)) return;

    const updated = {
      ...dependencies,
      [moduleId]: [...currentDeps, newDependency],
    };
    onChange(updated);
    setNewDependency('');
    setAddingFor(null);
  };

  const handleRemoveDependency = (moduleId, depId) => {
    const currentDeps = dependencies[moduleId] || DEFAULT_DEPENDENCIES[moduleId] || [];
    const updated = {
      ...dependencies,
      [moduleId]: currentDeps.filter(d => d !== depId),
    };
    // Remove empty arrays
    if (updated[moduleId].length === 0) {
      delete updated[moduleId];
    }
    onChange(updated);
  };

  const resetToDefaults = () => {
    onChange({});
  };

  // Check for missing dependencies in selectedModules
  const getMissingDependencies = () => {
    const missing = [];
    selectedModules.forEach(moduleId => {
      const deps = effectiveDependencies[moduleId] || [];
      deps.forEach(depId => {
        if (!selectedModules.includes(depId)) {
          missing.push({ module: moduleId, requires: depId });
        }
      });
    });
    return missing;
  };

  const missingDeps = getMissingDependencies();
  const hasCustomDependencies = Object.keys(dependencies).length > 0;

  // Modules that can have dependencies (exclude dashboard)
  const configurableModules = features.filter(m => m.id !== 'dashboard');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">
          Définissez les modules requis pour chaque fonctionnalité.
        </p>
        {hasCustomDependencies && (
          <button
            type="button"
            onClick={resetToDefaults}
            className="text-xs text-slate-400 hover:text-white transition-colors"
          >
            Réinitialiser par défaut
          </button>
        )}
      </div>

      {/* Missing dependencies warning */}
      {missingDeps.length > 0 && (
        <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-yellow-400 font-medium text-sm mb-2">
            <AlertCircle size={16} />
            Dépendances manquantes
          </div>
          <div className="space-y-1">
            {missingDeps.map((dep, idx) => (
              <div key={idx} className="text-xs text-yellow-300">
                <span className="font-medium">{getModuleName(dep.module)}</span>
                {' nécessite '}
                <span className="font-medium">{getModuleName(dep.requires)}</span>
                {' (non sélectionné)'}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dependencies list */}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {configurableModules.map(module => {
          const deps = effectiveDependencies[module.id] || [];
          const isCustom = dependencies[module.id] !== undefined;
          const isAddingHere = addingFor === module.id;

          return (
            <div
              key={module.id}
              className={`p-3 rounded-lg border transition-colors ${
                isCustom ? 'bg-slate-700/50 border-blue-500/50' : 'bg-slate-700/30 border-slate-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Link2 size={14} className="text-slate-400" />
                  <span className="text-sm font-medium text-white">{module.label}</span>
                  {isCustom && (
                    <span className="text-xs bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded">
                      personnalisé
                    </span>
                  )}
                </div>
                {!isAddingHere && (
                  <button
                    type="button"
                    onClick={() => setAddingFor(module.id)}
                    className="p-1 text-slate-400 hover:text-white hover:bg-slate-600 rounded transition-colors"
                    title="Ajouter une dépendance"
                  >
                    <Plus size={14} />
                  </button>
                )}
              </div>

              {/* Current dependencies */}
              {deps.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {deps.map(depId => (
                    <span
                      key={depId}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-slate-600 text-slate-200 rounded text-xs group"
                    >
                      <ArrowRight size={10} className="text-slate-400" />
                      {getModuleName(depId)}
                      <button
                        type="button"
                        onClick={() => handleRemoveDependency(module.id, depId)}
                        className="ml-1 text-slate-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {deps.length === 0 && !isAddingHere && (
                <div className="mt-1 text-xs text-slate-500">Aucune dépendance</div>
              )}

              {/* Add dependency form */}
              {isAddingHere && (
                <div className="mt-2 flex items-center gap-2">
                  <select
                    value={newDependency}
                    onChange={(e) => setNewDependency(e.target.value)}
                    className="flex-1 px-2 py-1.5 bg-slate-600 border border-slate-500 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sélectionner un module...</option>
                    {features
                      .filter(m => m.id !== module.id && !deps.includes(m.id))
                      .map(m => (
                        <option key={m.id} value={m.id}>{m.label}</option>
                      ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => handleAddDependency(module.id)}
                    disabled={!newDependency}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:text-slate-400 text-white rounded text-sm transition-colors"
                  >
                    Ajouter
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAddingFor(null); setNewDependency(''); }}
                    className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info box */}
      <div className="p-3 bg-slate-700/30 rounded-lg text-xs text-slate-400">
        <strong className="text-slate-300">Note:</strong> Quand un module a des dépendances,
        les modules requis seront automatiquement suggérés lors de la sélection des modules
        dans le profil.
      </div>
    </div>
  );
}

export default ModuleDependencyEditor;
